import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Image as ImageIcon,
  LoaderCircle,
  Play,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import type { Attachment, Milestone, TimelineEvent, Tone, WorkflowState } from "@repair/contracts";
import { bytesLabel, formatDateTime, waitingLabels } from "@repair/contracts";

export function AppMark({ compact = false }: { compact?: boolean }) {
  return (
    <div className="ui-mark" aria-label="售后维修系统">
      <span className="ui-mark__glyph" aria-hidden="true">R</span>
      {!compact && <span><strong>返修服务</strong><small>Service Workspace</small></span>}
    </div>
  );
}

export function Button({
  variant = "primary",
  busy,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  busy?: boolean;
}) {
  return (
    <button className={clsx("ui-button", `ui-button--${variant}`, className)} disabled={busy || props.disabled} {...props}>
      {busy && <LoaderCircle className="ui-spin" size={16} aria-hidden="true" />}
      <span>{children}</span>
    </button>
  );
}

export function IconButton({
  label,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return <button className="ui-icon-button" aria-label={label} title={label} {...props}>{children}</button>;
}

type PreviewableAttachment = Attachment & { mediaKind: "image" | "video" };

export function AttachmentGallery({ items }: { items: Attachment[] }) {
  const previewableItems = useMemo(
    () => items
      .map((item) => ({ ...item, mediaKind: attachmentMediaKind(item) }))
      .filter((item): item is PreviewableAttachment => item.mediaKind !== "file" && Boolean(item.temp_url)),
    [items],
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [failedIds, setFailedIds] = useState<Set<string>>(() => new Set());
  const activeIndex = previewableItems.findIndex((item) => item.id === activeId);
  const activeItem = activeIndex >= 0 ? previewableItems[activeIndex] : null;

  useEffect(() => {
    if (activeId && activeIndex < 0) setActiveId(null);
  }, [activeId, activeIndex]);

  function move(offset: number) {
    if (!previewableItems.length) return;
    const nextIndex = (activeIndex + offset + previewableItems.length) % previewableItems.length;
    setActiveId(previewableItems[nextIndex].id);
  }

  function markFailed(id: string) {
    setFailedIds((current) => new Set(current).add(id));
  }

  return (
    <>
      <div className="ui-attachment-gallery">
        {items.map((item) => {
          const mediaKind = attachmentMediaKind(item);
          const canPreview = mediaKind !== "file" && Boolean(item.temp_url);
          const visual = (
            <span className={clsx("ui-attachment-tile__visual", `is-${mediaKind}`)}>
              {mediaKind === "image" && item.temp_url && !failedIds.has(item.id)
                ? <img src={item.temp_url} alt="" loading="lazy" onError={() => markFailed(item.id)} />
                : mediaKind === "video"
                  ? <><Play size={22} fill="currentColor" /><span>视频</span></>
                  : <FileText size={22} />}
            </span>
          );
          const detail = (
            <span className="ui-attachment-tile__detail">
              <strong title={item.file_name}>{item.file_name}</strong>
              <small>{bytesLabel(item.file_size)} · {canPreview ? "点击预览" : "下载查看"}</small>
            </span>
          );

          return canPreview ? (
            <button
              type="button"
              className="ui-attachment-tile"
              key={item.id}
              onClick={() => setActiveId(item.id)}
              aria-label={`预览 ${item.file_name}`}
            >
              {visual}
              {detail}
            </button>
          ) : (
            <a
              className="ui-attachment-tile"
              href={item.temp_url}
              target="_blank"
              rel="noreferrer"
              key={item.id}
              aria-label={`下载 ${item.file_name}`}
            >
              {visual}
              {detail}
            </a>
          );
        })}
      </div>

      <Dialog.Root open={Boolean(activeItem)} onOpenChange={(open) => !open && setActiveId(null)}>
        {activeItem && (
          <Dialog.Portal>
            <Dialog.Overlay asChild>
              <motion.div className="ui-media-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} />
            </Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.section
                className="ui-media-viewer"
                initial={{ opacity: 0, scale: 0.985 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.18 }}
                onKeyDown={(event) => {
                  if (event.key === "ArrowLeft") move(-1);
                  if (event.key === "ArrowRight") move(1);
                }}
              >
                <div className="ui-media-viewer__stage">
                  {failedIds.has(activeItem.id) ? (
                    <div className="ui-media-viewer__error">
                      <FileText size={30} />
                      <strong>浏览器无法预览此文件</strong>
                      <span>可以下载原文件后查看</span>
                    </div>
                  ) : activeItem.mediaKind === "image" ? (
                    <img
                      src={activeItem.temp_url}
                      alt={activeItem.file_name}
                      onError={() => markFailed(activeItem.id)}
                    />
                  ) : (
                    <video
                      key={activeItem.id}
                      src={activeItem.temp_url}
                      controls
                      playsInline
                      preload="metadata"
                      onError={() => markFailed(activeItem.id)}
                    >
                      当前浏览器不支持视频播放。
                    </video>
                  )}

                  {previewableItems.length > 1 && (
                    <>
                      <IconButton label="上一个附件" className="ui-media-viewer__previous" onClick={() => move(-1)}>
                        <ChevronLeft size={24} />
                      </IconButton>
                      <IconButton label="下一个附件" className="ui-media-viewer__next" onClick={() => move(1)}>
                        <ChevronRight size={24} />
                      </IconButton>
                    </>
                  )}
                </div>

                <aside className="ui-media-viewer__info">
                  <header>
                    <span className="ui-media-viewer__kind">
                      {activeItem.mediaKind === "image" ? <ImageIcon size={16} /> : <Play size={16} />}
                      {activeItem.mediaKind === "image" ? "照片" : "视频"}
                    </span>
                    <Dialog.Close asChild><IconButton label="关闭预览"><X size={20} /></IconButton></Dialog.Close>
                  </header>
                  <div>
                    <Dialog.Title>{activeItem.file_name}</Dialog.Title>
                    <Dialog.Description>
                      {bytesLabel(activeItem.file_size)}
                      {previewableItems.length > 1 ? ` · ${activeIndex + 1} / ${previewableItems.length}` : ""}
                    </Dialog.Description>
                  </div>
                  <a
                    className="ui-media-viewer__download"
                    href={activeItem.temp_url}
                    target="_blank"
                    rel="noreferrer"
                    download={activeItem.file_name}
                  >
                    <Download size={17} />
                    下载原文件
                  </a>
                </aside>
              </motion.section>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </Dialog.Root>
    </>
  );
}

function attachmentMediaKind(item: Attachment): "image" | "video" | "file" {
  const mimeType = String(item.mime_type || "").toLowerCase();
  const fileName = String(item.file_name || "").toLowerCase();
  if (mimeType.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic|heif)$/.test(fileName)) return "image";
  if (mimeType.startsWith("video/") || /\.(mp4|mov|webm|m4v)$/.test(fileName)) return "video";
  return "file";
}

export function StatusBadge({ label, tone = "neutral" }: { label: string; tone?: Tone }) {
  return <span className={clsx("ui-status", `ui-status--${tone}`)}>{label}</span>;
}

export function WaitingChip({ value }: { value?: string | null }) {
  if (!value) return null;
  return <span className="ui-waiting">{waitingLabels[value] || value}</span>;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="ui-field">
      <span className="ui-field__label">{label}</span>
      {children}
      {error ? <span className="ui-field__error">{error}</span> : hint ? <span className="ui-field__hint">{hint}</span> : null}
    </label>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="ui-input" {...props} />;
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="ui-textarea" rows={4} {...props} />;
}

export function PageBanner({
  tone = "info",
  title,
  children,
}: {
  tone?: Tone;
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className={clsx("ui-banner", `ui-banner--${tone}`)} role={tone === "danger" ? "alert" : "status"}>
      <AlertCircle size={18} aria-hidden="true" />
      <div>{title && <strong>{title}</strong>}<span>{children}</span></div>
    </div>
  );
}

export function Section({
  title,
  meta,
  action,
  children,
  className,
}: {
  title?: string;
  meta?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx("ui-section", className)}>
      {(title || action) && <header className="ui-section__header"><div>{title && <h2>{title}</h2>}{meta && <p>{meta}</p>}</div>{action}</header>}
      {children}
    </section>
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="ui-empty">
      <span className="ui-empty__symbol" aria-hidden="true"><Check size={22} /></span>
      <strong>{title}</strong>
      {detail && <p>{detail}</p>}
      {action}
    </div>
  );
}

export function LoadingState({ label = "正在加载" }: { label?: string }) {
  return <div className="ui-loading" role="status"><LoaderCircle className="ui-spin" size={20} />{label}</div>;
}

export function LifecycleProgress({
  milestones,
  compact = false,
}: {
  milestones: Milestone[];
  compact?: boolean;
}) {
  const visible = compact
    ? collapseMilestones(milestones)
    : milestones;
  return (
    <ol className={clsx("ui-lifecycle", compact && "ui-lifecycle--compact")} aria-label="维修进度">
      {visible.map((item) => (
        <li key={item.key} className={clsx("ui-lifecycle__item", `is-${item.status}`)} aria-current={item.status === "current" ? "step" : undefined}>
          <span className="ui-lifecycle__dot">{item.status === "completed" ? <Check size={12} /> : item.order}</span>
          <span>{compact ? item.mobile_macro : item.admin_label}</span>
        </li>
      ))}
    </ol>
  );
}

function collapseMilestones(milestones: Milestone[]) {
  const order = ["报修", "寄回", "检修", "报价", "维修", "回寄", "完成"];
  return order.map((label, index) => {
    const group = milestones.filter((item) => item.mobile_macro === label);
    const current = group.find((item) => item.status === "current");
    const allComplete = group.length > 0 && group.every((item) => item.status === "completed" || item.status === "conditional");
    return {
      key: label,
      order: index + 1,
      customer_label: label,
      admin_label: label,
      mobile_macro: label,
      conditional: false,
      status: current ? "current" : allComplete ? "completed" : "pending",
    } satisfies Milestone;
  });
}

export function StatusHero({
  state,
  orderNo,
  children,
}: {
  state: WorkflowState;
  orderNo?: string;
  children?: ReactNode;
}) {
  return (
    <section className="ui-status-hero">
      <div className="ui-status-hero__eyebrow">
        {orderNo && <code>{orderNo}</code>}
        <StatusBadge label={state.label} tone={state.tone} />
      </div>
      <h1>{state.label}</h1>
      <div className="ui-status-hero__meta">
        <WaitingChip value={state.waiting_on} />
        {!state.terminal && <span>状态更新后会在这里保留记录</span>}
      </div>
      {children}
    </section>
  );
}

export function EventTimeline({ events }: { events: TimelineEvent[] }) {
  if (!events.length) return <EmptyState title="暂无处理记录" detail="发生业务动作后会显示在这里。" />;
  return (
    <ol className="ui-timeline">
      {events.map((event) => (
        <li key={event.id}>
          <span className="ui-timeline__dot" aria-hidden="true" />
          <div>
            <strong>{event.action_note || event.status_name || "工单更新"}</strong>
            <p>{event.operator_name || event.operator_role || "系统"} · {formatDateTime(event.created_at)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay asChild>
          <motion.div className="ui-dialog-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />
        </Dialog.Overlay>
        <Dialog.Content asChild>
          <motion.section className="ui-drawer" initial={{ x: 28, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ duration: 0.22 }}>
            <header>
              <div><Dialog.Title>{title}</Dialog.Title>{description && <Dialog.Description>{description}</Dialog.Description>}</div>
              <Dialog.Close asChild><IconButton label="关闭"><X size={20} /></IconButton></Dialog.Close>
            </header>
            <div className="ui-drawer__body">{children}</div>
          </motion.section>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild><motion.div className="ui-dialog-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} /></Dialog.Overlay>
            <Dialog.Content asChild>
              <motion.section className="ui-dialog" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}>
                <header className="ui-dialog__header">
                  <div>
                    <Dialog.Title>{title}</Dialog.Title>
                    {description && <Dialog.Description>{description}</Dialog.Description>}
                  </div>
                  <Dialog.Close asChild><IconButton label="关闭"><X size={20} /></IconButton></Dialog.Close>
                </header>
                <div className="ui-dialog__body">{children}</div>
                <footer className="ui-dialog__footer">
                  <Dialog.Close asChild><Button variant="secondary">取消</Button></Dialog.Close>
                  <Button variant={danger ? "danger" : "primary"} busy={busy} onClick={onConfirm}>{confirmLabel}</Button>
                </footer>
              </motion.section>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

export function RowLink({ children }: { children: ReactNode }) {
  return <span className="ui-row-link">{children}<ChevronRight size={16} /></span>;
}

export function PrimaryActionHint({ label }: { label: string }) {
  return <span className="ui-action-hint">{label}<ArrowRight size={15} /></span>;
}
